import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Clock,
  Calendar,
  AlertTriangle,
  CheckSquare,
  FileText,
  ExternalLink,
  Download,
  AlertCircle,
  Sparkles,
  RefreshCw,
  Flag,
  Plus,
  Loader2,
  ArrowLeft,
  ChevronRight,
  Trash2,
  Check
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ================================================
// COUNTDOWN TIMER HOOK
// ================================================
function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    if (!targetDate) return;
    
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = new Date(targetDate) - now;

      if (difference > 0) {
        return {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        };
      }
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
}

// ================================================
// SUB-COMPONENTS
// ================================================

// Countdown Timer Display
function CountdownTimer({ deadline }) {
  const timeLeft = useCountdown(deadline);
  
  if (!deadline) {
    return (
      <div className="text-blue-100 text-center">
        <p>No deadline set</p>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2" data-testid="countdown-timer">
      {/* Days */}
      <div className="text-center">
        <span className="text-5xl font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {String(timeLeft.days).padStart(2, '0')}
        </span>
        <p className="text-xs text-blue-100 uppercase tracking-wider mt-1">Days</p>
      </div>
      
      {/* Separator */}
      <div className="flex flex-col gap-1.5 mx-1">
        <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
        <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
      </div>
      
      {/* Hours */}
      <div className="text-center">
        <span className="text-5xl font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {String(timeLeft.hours).padStart(2, '0')}
        </span>
        <p className="text-xs text-blue-100 uppercase tracking-wider mt-1">Hours</p>
      </div>
      
      {/* Separator */}
      <div className="flex flex-col gap-1.5 mx-1">
        <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
        <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
      </div>
      
      {/* Minutes */}
      <div className="text-center">
        <span className="text-5xl font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {String(timeLeft.minutes).padStart(2, '0')}
        </span>
        <p className="text-xs text-blue-100 uppercase tracking-wider mt-1">Mins</p>
      </div>
    </div>
  );
}

// Key Deadlines Card
function KeyDeadlinesCard({ deadlines, onToggle, onDelete }) {
  if (!deadlines || deadlines.length === 0) {
    return (
      <Card className="border-slate-200 bg-white shadow-sm h-full">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Key Deadlines
            </CardTitle>
            <Clock className="w-4 h-4 text-slate-400" />
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-center py-8 text-slate-500">
            <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p>No deadlines added yet</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="border-slate-200 bg-white shadow-sm h-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Key Deadlines
          </CardTitle>
          <Clock className="w-4 h-4 text-slate-400" />
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-5">
          {deadlines.map((deadline, index) => (
            <div key={deadline.id} className="relative pl-6 group" data-testid={`deadline-${deadline.id}`}>
              {/* Timeline dot and line */}
              <div className={cn(
                "absolute left-0 top-1 w-3 h-3 rounded-full border-2",
                deadline.is_complete 
                  ? "bg-emerald-500 border-emerald-500" 
                  : deadline.is_primary 
                    ? "bg-blue-500 border-blue-500" 
                    : "bg-emerald-500 border-emerald-500"
              )} />
              {index < deadlines.length - 1 && (
                <div className="absolute left-1.5 top-4 w-px h-12 bg-slate-200" />
              )}
              
              {/* Content */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className={cn(
                    "text-xs font-medium",
                    deadline.is_primary ? "text-blue-600" : "text-emerald-600"
                  )}>
                    {new Date(deadline.deadline_date).toLocaleDateString('en-US', { 
                      month: 'short', day: 'numeric', year: 'numeric' 
                    })} • {new Date(deadline.deadline_date).toLocaleTimeString('en-US', { 
                      hour: 'numeric', minute: '2-digit', timeZoneName: 'short' 
                    })}
                  </p>
                  <h4 className={cn(
                    "font-semibold mt-1",
                    deadline.is_complete ? "text-slate-500 line-through" : "text-slate-900"
                  )} style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {deadline.title}
                    {deadline.is_primary && (
                      <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">PRIMARY</Badge>
                    )}
                  </h4>
                  {deadline.description && (
                    <p className="text-sm text-slate-500 mt-0.5">
                      {deadline.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => onToggle(deadline.id, deadline.is_complete)}
                    className="h-7 w-7 p-0"
                  >
                    <Check className={cn("w-4 h-4", deadline.is_complete ? "text-emerald-600" : "text-slate-400")} />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => onDelete(deadline.id)}
                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Formatting Requirements Card
function FormattingRequirementsCard({ requirements, onDelete }) {
  if (!requirements || requirements.length === 0) {
    return (
      <Card className="border-slate-200 bg-white shadow-sm h-full">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Formatting Requirements
            </CardTitle>
            <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
              <Sparkles className="w-3 h-3 mr-1" />
              Manual Entry
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-center py-8 text-slate-500">
            <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p>No formatting requirements added</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="border-slate-200 bg-white shadow-sm h-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Formatting Requirements
          </CardTitle>
          <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
            <Sparkles className="w-3 h-3 mr-1" />
            {requirements.some(r => r.is_ai_extracted) ? 'AI Extracted' : 'Manual Entry'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {requirements.map((req) => (
            <div 
              key={req.id} 
              className={cn(
                "p-3 rounded-lg border group relative",
                req.severity === 'CRITICAL' && "bg-red-50 border-red-100",
                req.severity === 'STRICT' && "bg-amber-50 border-amber-100",
                req.severity === 'INFO' && "bg-slate-50 border-slate-100"
              )}
              data-testid={`formatting-${req.id}`}
            >
              <div className="flex items-start gap-2">
                {(req.severity === 'CRITICAL' || req.severity === 'STRICT') && (
                  <AlertTriangle className={cn(
                    "w-4 h-4 mt-0.5 flex-shrink-0",
                    req.severity === 'CRITICAL' ? "text-red-500" : "text-amber-500"
                  )} />
                )}
                <div className="flex-1">
                  <p className={cn(
                    "text-xs font-semibold uppercase tracking-wide",
                    req.severity === 'CRITICAL' && "text-red-600",
                    req.severity === 'STRICT' && "text-amber-600",
                    req.severity === 'INFO' && "text-slate-500"
                  )}>
                    {req.severity === 'CRITICAL' ? 'DISQUALIFICATION RISK' : 
                     req.severity === 'STRICT' ? 'STRICT RULE' : 
                     req.requirement_type || 'INFO'}
                  </p>
                  <p className="font-medium text-slate-900 mt-1">{req.title}</p>
                  {req.description && (
                    <p className="text-xs text-slate-500 mt-0.5">{req.description}</p>
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => onDelete(req.id)}
                  className="h-7 w-7 p-0 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Red Flag Notification Banner
function RedFlagBanner({ flags, onAcknowledge, onDelete }) {
  if (!flags || flags.length === 0) return null;
  
  const unacknowledged = flags.filter(f => !f.is_acknowledged);
  if (unacknowledged.length === 0) return null;
  
  return (
    <div className="mb-6 space-y-3">
      {unacknowledged.map((flag) => (
        <Card key={flag.id} className="border-red-200 bg-red-50 shadow-sm" data-testid={`red-flag-${flag.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Flag className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-red-600 text-white hover:bg-red-600 text-xs">
                    {flag.flag_type}
                  </Badge>
                  <span className="text-xs text-red-500">
                    {new Date(flag.detected_at).toLocaleString()}
                  </span>
                </div>
                <h4 className="font-semibold text-red-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {flag.title}
                </h4>
                {flag.description && (
                  <p className="text-sm text-red-700 mt-0.5">{flag.description}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="border-red-300 text-red-700 hover:bg-red-100"
                  onClick={() => onAcknowledge(flag.id)}
                >
                  Acknowledge
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => onDelete(flag.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Compliance Checklist Card (from existing compliance items)
function ComplianceChecklistCard({ pursuitId, getAuthHeaders }) {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}/compliance`, {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          setItems(await response.json());
        }
      } catch (err) {
        console.error('Failed to fetch compliance items:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchItems();
  }, [pursuitId, getAuthHeaders]);
  
  const handleToggle = async (itemId, currentState) => {
    setItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, is_complete: !currentState } : item
    ));
    
    try {
      await fetch(`${API_URL}/api/pursuits/${pursuitId}/compliance/${itemId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_complete: !currentState })
      });
    } catch (err) {
      setItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, is_complete: currentState } : item
      ));
    }
  };
  
  const completedCount = items.filter(i => i.is_complete).length;
  const volumeItems = items.filter(i => i.category === 'VOLUME');
  
  if (isLoading) {
    return (
      <Card className="border-slate-200 bg-white shadow-sm h-full">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="border-slate-200 bg-white shadow-sm h-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Deliverables Checklist
          </CardTitle>
          <CheckSquare className="w-4 h-4 text-slate-400" />
        </div>
        <p className="text-xs text-slate-500">
          {completedCount} of {items.length} complete
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {volumeItems.length > 0 ? volumeItems.map((item) => (
            <div 
              key={item.id} 
              className={cn(
                "p-3 rounded-lg border transition-all cursor-pointer hover:border-blue-200",
                item.is_complete ? "bg-white border-slate-100" : "bg-slate-50 border-slate-200"
              )}
              onClick={() => handleToggle(item.id, item.is_complete)}
              data-testid={`deliverable-${item.id}`}
            >
              <div className="flex items-start gap-3">
                <Checkbox 
                  checked={item.is_complete}
                  className={cn(
                    "mt-0.5",
                    item.is_complete && "bg-blue-600 border-blue-600 text-white"
                  )}
                />
                <div className="flex-1">
                  <h4 className={cn(
                    "font-medium",
                    item.is_complete ? "text-slate-900" : "text-slate-700"
                  )} style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {item.item_name}
                  </h4>
                  {item.item_description && (
                    <p className="text-xs text-slate-500 mt-0.5">{item.item_description}</p>
                  )}
                  {item.is_required && (
                    <Badge className="mt-2 bg-red-50 text-red-600 border border-red-200 text-xs font-normal hover:bg-red-50">
                      REQUIRED
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )) : (
            <div className="text-center py-4 text-slate-500">
              <CheckSquare className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No deliverables added yet</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ================================================
// MAIN COMPONENT
// ================================================
// v3.11.8 — accepts `embedded` prop. When true, renders only the page body
// (skips the outer breadcrumb + page-header block) so it can be mounted as
// a tab inside Compliance Tracker without a second header.
export default function SubmissionGuideLive({ embedded = false } = {}) {
  const { workspaceId, pursuitId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  // Data state
  const [guideData, setGuideData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Modal states
  const [isDeadlineModalOpen, setIsDeadlineModalOpen] = useState(false);
  const [isFormattingModalOpen, setIsFormattingModalOpen] = useState(false);
  const [isRedFlagModalOpen, setIsRedFlagModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form states
  const [deadlineForm, setDeadlineForm] = useState({
    title: '',
    description: '',
    deadline_date: '',
    time_zone: 'EST',
    is_primary: false
  });
  
  const [formattingForm, setFormattingForm] = useState({
    title: '',
    description: '',
    requirement_type: 'INFO',
    severity: 'INFO'
  });
  
  const [redFlagForm, setRedFlagForm] = useState({
    flag_type: 'AMENDMENT',
    title: '',
    description: '',
    severity: 'HIGH'
  });

  // Fetch submission guide data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}/submission-guide`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setGuideData(data);
        
        // Seed defaults if empty
        if (data.deadlines.length === 0 && data.formatting_requirements.length === 0) {
          await fetch(`${API_URL}/api/pursuits/${pursuitId}/submission-guide/seed-defaults`, {
            method: 'POST',
            headers: getAuthHeaders()
          });
          // Refetch after seeding
          const refreshResponse = await fetch(`${API_URL}/api/pursuits/${pursuitId}/submission-guide`, {
            headers: getAuthHeaders()
          });
          if (refreshResponse.ok) {
            setGuideData(await refreshResponse.json());
          }
        }
      } else {
        toast.error('Failed to load submission guide');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error('Failed to load submission guide');
    } finally {
      setIsLoading(false);
    }
  }, [pursuitId, getAuthHeaders]);

  useEffect(() => {
    if (pursuitId) fetchData();
  }, [pursuitId, fetchData]);

  // Find primary deadline for countdown
  const primaryDeadline = useMemo(() => {
    if (!guideData?.deadlines) return guideData?.response_deadline;
    const primary = guideData.deadlines.find(d => d.is_primary);
    return primary?.deadline_date || guideData?.response_deadline;
  }, [guideData]);

  // Calculate days until deadline
  const daysUntilDeadline = useMemo(() => {
    if (!primaryDeadline) return null;
    const now = new Date();
    const difference = new Date(primaryDeadline) - now;
    return Math.ceil(difference / (1000 * 60 * 60 * 24));
  }, [primaryDeadline]);

  // ========== HANDLERS ==========
  
  // Deadline handlers
  const handleAddDeadline = async () => {
    if (!deadlineForm.title.trim() || !deadlineForm.deadline_date) {
      toast.error('Title and date are required');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}/deadlines`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...deadlineForm,
          deadline_date: new Date(deadlineForm.deadline_date).toISOString()
        })
      });
      
      if (response.ok) {
        toast.success('Deadline added');
        setIsDeadlineModalOpen(false);
        setDeadlineForm({ title: '', description: '', deadline_date: '', time_zone: 'EST', is_primary: false });
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to add deadline');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleToggleDeadline = async (id, currentState) => {
    try {
      await fetch(`${API_URL}/api/pursuits/${pursuitId}/deadlines/${id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_complete: !currentState })
      });
      fetchData();
    } catch (err) {
      toast.error('Failed to update deadline');
    }
  };
  
  const handleDeleteDeadline = async (id) => {
    if (!window.confirm('Delete this deadline?')) return;
    try {
      await fetch(`${API_URL}/api/pursuits/${pursuitId}/deadlines/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      fetchData();
      toast.success('Deadline deleted');
    } catch (err) {
      toast.error('Failed to delete deadline');
    }
  };
  
  // Formatting handlers
  const handleAddFormatting = async () => {
    if (!formattingForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}/formatting`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formattingForm)
      });
      
      if (response.ok) {
        toast.success('Requirement added');
        setIsFormattingModalOpen(false);
        setFormattingForm({ title: '', description: '', requirement_type: 'INFO', severity: 'INFO' });
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to add requirement');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleDeleteFormatting = async (id) => {
    if (!window.confirm('Delete this requirement?')) return;
    try {
      await fetch(`${API_URL}/api/pursuits/${pursuitId}/formatting/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      fetchData();
      toast.success('Requirement deleted');
    } catch (err) {
      toast.error('Failed to delete requirement');
    }
  };
  
  // Red flag handlers
  const handleAddRedFlag = async () => {
    if (!redFlagForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}/red-flags`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(redFlagForm)
      });
      
      if (response.ok) {
        toast.success('Red flag added');
        setIsRedFlagModalOpen(false);
        setRedFlagForm({ flag_type: 'AMENDMENT', title: '', description: '', severity: 'HIGH' });
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to add red flag');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleAcknowledgeRedFlag = async (id) => {
    try {
      await fetch(`${API_URL}/api/pursuits/${pursuitId}/red-flags/${id}/acknowledge`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      fetchData();
      toast.success('Red flag acknowledged');
    } catch (err) {
      toast.error('Failed to acknowledge');
    }
  };

  // v3.11.24 — Mark pursuit as submitted (auto-flips status DRAFTING/etc → SUBMITTED).
  const handleMarkSubmitted = async () => {
    if (!window.confirm(
      'Mark this pursuit as SUBMITTED?\n\nThis records the submission timestamp ' +
      'and moves the pursuit out of the active pipeline. Use this only after the ' +
      'final proposal package has actually been delivered to the agency.'
    )) return;
    try {
      const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}/mark-submitted`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        toast.success('Pursuit marked as SUBMITTED');
        // Notify other panels (Pipeline, dashboards) so they refetch.
        window.dispatchEvent(new CustomEvent('pursuit-status-changed', {
          detail: { pursuitId, newStatus: 'SUBMITTED' }
        }));
        fetchData();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err?.detail || 'Failed to mark as submitted');
      }
    } catch (err) {
      toast.error('Failed to mark as submitted');
    }
  };
  
  const handleDeleteRedFlag = async (id) => {
    if (!window.confirm('Delete this red flag?')) return;
    try {
      await fetch(`${API_URL}/api/pursuits/${pursuitId}/red-flags/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      fetchData();
      toast.success('Red flag deleted');
    } catch (err) {
      toast.error('Failed to delete red flag');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!guideData) {
    return (
      <div className="p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-red-700">Pursuit not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="submission-guide-live" className={cn(!embedded && 'min-h-screen bg-slate-50')}>
      {/* Header — hidden when embedded inside Compliance Tracker tab */}
      {!embedded && (
      <div className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 px-2"
                onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/proposal`)}
              >
                <ArrowLeft className="w-3 h-3 mr-1" />
                Proposal
              </Button>
              <ChevronRight className="w-4 h-4" />
              <span className="text-slate-700">Submission Guide</span>
            </div>
            <div className="flex items-center gap-3">
              {guideData.agency_code && (
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">{guideData.agency_code.slice(0, 3)}</span>
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {guideData.opportunity_name || 'Submission Guide'}
                </h1>
                <p className="text-sm text-slate-500">
                  Submission Guide {daysUntilDeadline !== null && `• Due in ${daysUntilDeadline} Days`}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsRedFlagModalOpen(true)}>
              <Flag className="w-4 h-4 mr-2 text-red-500" />
              Add Alert
            </Button>
          </div>
        </div>
      </div>
      )}

      {/* v3.18.20 — body centered to max-w-7xl for non-embedded standalone
          view. When embedded inside Compliance Tracker, the parent already
          centers via its own max-w-7xl wrapper. */}
      <div className={embedded ? 'p-8' : 'p-8'}>
        <div className="max-w-7xl mx-auto">
        {/* Red Flag Notifications */}
        <RedFlagBanner 
          flags={guideData.red_flags} 
          onAcknowledge={handleAcknowledgeRedFlag}
          onDelete={handleDeleteRedFlag}
        />

        {/* Main Countdown Banner */}
        <Card className="bg-blue-600 border-0 shadow-lg mb-6 overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-6">
              {/* Countdown Timer */}
              <CountdownTimer deadline={primaryDeadline} />
              
              {/* Portal Info & Actions */}
              <div className="flex items-center gap-4 flex-wrap">
                {/* Portal Link */}
                {guideData.portal_url && (
                  <a 
                    href={guideData.portal_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-blue-500/50 hover:bg-blue-500/70 text-white px-4 py-2 rounded-full transition-colors"
                    data-testid="portal-link"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span className="font-medium">Open Portal</span>
                  </a>
                )}
                
                {/* Export to Calendar */}
                <Button 
                  variant="secondary" 
                  className="bg-white text-slate-700 hover:bg-slate-50"
                  data-testid="export-calendar-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Export to Calendar
                </Button>
              </div>
            </div>
            
            {/* Submission Note */}
            <p className="text-blue-100 text-sm mt-4 text-center">
              Must be submitted by Authorized Official
            </p>
          </CardContent>
        </Card>

        {/* Add buttons row */}
        <div className="flex items-center justify-between mb-6 px-2">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <span>Live Data from Database</span>
          </div>
          <div className="flex gap-2">
            {/* v3.11.24 — Mark as Submitted: the user-triggered tail of
                auto-progression. Sets pursuit.status = SUBMITTED and
                captures `submitted_at`. Disabled when the pursuit is
                already submitted/won/lost. */}
            {guideData?.pursuit_status &&
              !['SUBMITTED', 'WON', 'LOST', 'NO_BID'].includes(guideData.pursuit_status) && (
              <Button
                size="sm"
                onClick={handleMarkSubmitted}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="mark-submitted-btn"
              >
                <Check className="w-4 h-4 mr-1" />
                Mark as Submitted
              </Button>
            )}
            {guideData?.pursuit_status === 'SUBMITTED' && (
              <Badge className="bg-emerald-100 text-emerald-700 border-0 px-3 py-1.5">
                <Check className="w-3.5 h-3.5 mr-1" />
                Submitted
              </Badge>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsDeadlineModalOpen(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Deadline
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsFormattingModalOpen(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Formatting Req
            </Button>
          </div>
        </div>

        {/* Three-Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Key Deadlines */}
          <KeyDeadlinesCard 
            deadlines={guideData.deadlines} 
            onToggle={handleToggleDeadline}
            onDelete={handleDeleteDeadline}
          />
          
          {/* Formatting Requirements */}
          <FormattingRequirementsCard 
            requirements={guideData.formatting_requirements}
            onDelete={handleDeleteFormatting}
          />
          
          {/* Deliverables Checklist (from compliance) */}
          <ComplianceChecklistCard 
            pursuitId={pursuitId}
            getAuthHeaders={getAuthHeaders}
          />
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 p-4 bg-white rounded-lg border border-slate-200">
          <Button 
            variant="outline"
            onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/proposal`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Proposal
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => navigate(`/${workspaceId}/pipeline`)}
          >
            View Pipeline
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
        </div>
      </div>

      {/* Add Deadline Modal */}
      <Dialog open={isDeadlineModalOpen} onOpenChange={setIsDeadlineModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-deadline-modal">
          <DialogHeader>
            <DialogTitle>Add Deadline</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={deadlineForm.title}
                onChange={(e) => setDeadlineForm({...deadlineForm, title: e.target.value})}
                placeholder="e.g., Final Proposal Due"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={deadlineForm.description}
                onChange={(e) => setDeadlineForm({...deadlineForm, description: e.target.value})}
                placeholder="Additional details..."
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Date & Time *</Label>
              <Input
                type="datetime-local"
                value={deadlineForm.deadline_date}
                onChange={(e) => setDeadlineForm({...deadlineForm, deadline_date: e.target.value})}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                checked={deadlineForm.is_primary}
                onCheckedChange={(v) => setDeadlineForm({...deadlineForm, is_primary: v})}
              />
              <Label className="text-sm">This is the primary submission deadline</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeadlineModalOpen(false)}>Cancel</Button>
            <Button onClick={handleAddDeadline} disabled={isSubmitting} className="bg-teal-700 hover:bg-teal-800">
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Add Deadline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Formatting Modal */}
      <Dialog open={isFormattingModalOpen} onOpenChange={setIsFormattingModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-formatting-modal">
          <DialogHeader>
            <DialogTitle>Add Formatting Requirement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={formattingForm.title}
                onChange={(e) => setFormattingForm({...formattingForm, title: e.target.value})}
                placeholder="e.g., Font: 11pt Arial"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formattingForm.description}
                onChange={(e) => setFormattingForm({...formattingForm, description: e.target.value})}
                placeholder="Additional details..."
              />
            </div>
            <div className="space-y-2">
              <Label>Severity</Label>
              <Select value={formattingForm.severity} onValueChange={(v) => setFormattingForm({...formattingForm, severity: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CRITICAL">Critical (Disqualification Risk)</SelectItem>
                  <SelectItem value="STRICT">Strict Rule</SelectItem>
                  <SelectItem value="INFO">Informational</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormattingModalOpen(false)}>Cancel</Button>
            <Button onClick={handleAddFormatting} disabled={isSubmitting} className="bg-teal-700 hover:bg-teal-800">
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Add Requirement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Red Flag Modal */}
      <Dialog open={isRedFlagModalOpen} onOpenChange={setIsRedFlagModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-redflag-modal">
          <DialogHeader>
            <DialogTitle>Add Red Flag Alert</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Flag Type</Label>
              <Select value={redFlagForm.flag_type} onValueChange={(v) => setRedFlagForm({...redFlagForm, flag_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AMENDMENT">Amendment</SelectItem>
                  <SelectItem value="CORRECTION">Correction</SelectItem>
                  <SelectItem value="DEADLINE_CHANGE">Deadline Change</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={redFlagForm.title}
                onChange={(e) => setRedFlagForm({...redFlagForm, title: e.target.value})}
                placeholder="e.g., Amendment 003 Posted"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={redFlagForm.description}
                onChange={(e) => setRedFlagForm({...redFlagForm, description: e.target.value})}
                placeholder="Details about the change..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Severity</Label>
              <Select value={redFlagForm.severity} onValueChange={(v) => setRedFlagForm({...redFlagForm, severity: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="LOW">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRedFlagModalOpen(false)}>Cancel</Button>
            <Button onClick={handleAddRedFlag} disabled={isSubmitting} className="bg-red-600 hover:bg-red-700">
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Add Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
